/**
 * Which voice room each player is in - the step 8 party split. A Go copy lives
 * in the server's internal/voice with the same test table (rooms.test.ts);
 * change one, change both.
 *
 * Rooms are DERIVED, never stored: there is one roster per match, and a
 * player's room is a pure function of the lobby's public document and their
 * id. So a deal, or a table finishing, moves people between rooms without a
 * single write, and the server and every client agree because they read the
 * same directory in the same order. The roster's `room` field is the server's
 * own bookkeeping for its grace window; clients never read it.
 *
 * The rule is `firstPlayingInstance` (state/party.ts) applied to voice: a
 * player whose table has finished, or who was never dealt in, hears the table
 * they are being shown - the lowest-index one still playing.
 */
import { teamStyle } from "@fiveout/core";
import { desiredPulls, type MuteCheck, type VoiceRoom } from "./rules";

/** The parts of the lobby's public document a room is derived from. */
export interface RoomLobby {
  id: string;
  phase: string;
  party?: {
    instances: { gameId: string; playerIds: string[]; status: string }[];
  } | null;
  /** Who has stepped away to watch which table (the roster's `viewing`). Absent reads as nobody. */
  players?: { id: string; viewing?: string | null; team?: string | null }[];
  /** Who is watching instead of playing, and which table they have zoomed in on. Absent reads as nobody. */
  observers?: { id: string; viewing?: string | null }[];
  /** `"teams"` when the lobby plays in teams; absent is free-for-all. */
  mode?: string | null;
  /** The team ids in play. Absent or empty reads as no teams. */
  teams?: readonly string[] | null;
}

/**
 * The one room every observer and not-dealt-in player shares while a Teams
 * round is on. Mirrors WatchersRoom in the server's internal/voice.
 */
export const WATCHERS_ROOM = "watchers";

/** The room of one team while a Teams round is on. Mirrors TeamRoom in the server's internal/voice. */
export function teamRoom(teamId: string): string {
  return `team:${teamId}`;
}

/** Whether voice is split by team right now: a Teams lobby, mid-round. */
function teamsRound(lobby: RoomLobby): boolean {
  return (
    lobby.phase === "playing" &&
    lobby.mode === "teams" &&
    (lobby.teams?.length ?? 0) > 0
  );
}

/**
 * The Teams-mode rule, or null when it does not apply and the table rules
 * decide. Only mid-round: in the lobby phases everyone talks to everyone.
 * Mid-round a SEATED player is in their team's room - across every table of a
 * party and at a single table alike, overriding stepping away to watch another
 * table - and everybody else (observers at any zoom, pending players, late
 * joiners) is in WATCHERS_ROOM, so observers can never listen to a team. A
 * seated player with no valid team cannot happen once the lobby is
 * normalized; they fall back to the table rules.
 */
function teamPlace(
  lobby: RoomLobby,
  playerId: string,
): { room: string; observing: boolean } | null {
  if (!teamsRound(lobby)) return null;
  if (!lobby.observers?.some((o) => o.id === playerId)) {
    const me = lobby.players?.find((p) => p.id === playerId);
    const instances = lobby.party?.instances ?? [];
    // A party deals everyone into an instance; a player it did not deal
    // (arrived after the deal) is not seated at any table.
    if (
      me &&
      (instances.length === 0 ||
        instances.some((i) => i.playerIds.includes(playerId)))
    ) {
      if (!me.team || !lobby.teams!.includes(me.team)) return null;
      return { room: teamRoom(me.team), observing: false };
    }
  }
  return { room: WATCHERS_ROOM, observing: true };
}

/**
 * The room of an observer looking at every table of a split party at once:
 * theirs alone, so voice is off there. Mirrors AloneRoom in the server's
 * internal/voice.
 */
export function aloneRoom(playerId: string): string {
  return `alone:${playerId}`;
}

function place(
  lobby: RoomLobby,
  playerId: string,
): { room: string; observing: boolean } {
  const team = teamPlace(lobby, playerId);
  if (team) return team;
  const instances = lobby.party?.instances ?? [];
  // One table is not a split: everybody stays in the lobby's room, which is
  // what a single table always was.
  if (lobby.phase !== "playing" || instances.length < 2)
    return { room: lobby.id, observing: false };
  // An observer by choice has no table of their own: zoomed in on a table
  // still playing, they are in its room as a watcher; looking at every table
  // at once, voice is off - a room nobody else derives to.
  const watcher = lobby.observers?.find((o) => o.id === playerId);
  if (watcher) {
    const zoomed = watcher.viewing
      ? instances.find(
          (i) => i.gameId === watcher.viewing && i.status === "playing",
        )
      : null;
    return {
      room: zoomed ? zoomed.gameId : aloneRoom(playerId),
      observing: true,
    };
  }
  const own = instances.find((i) => i.playerIds.includes(playerId));
  if (own && own.status === "playing") {
    // Stepped away to watch another table that is still playing: that table is
    // who they see, so it is who they hear and who hears them. A `viewing` left
    // on a finished table, or on their own, is home.
    const viewing = lobby.players?.find((p) => p.id === playerId)?.viewing;
    const away =
      viewing && viewing !== own.gameId
        ? instances.find((i) => i.gameId === viewing && i.status === "playing")
        : null;
    if (away) return { room: away.gameId, observing: true };
    return { room: own.gameId, observing: false };
  }
  const watched = instances.find((i) => i.status === "playing");
  if (watched) return { room: watched.gameId, observing: true };
  // Every table finished and the fan-in has not turned the lobby to
  // round-over yet: that is where everyone is about to be.
  return { room: lobby.id, observing: false };
}

export function roomOf(lobby: RoomLobby, playerId: string): string {
  return place(lobby, playerId).room;
}

/** In a table's room as a watcher rather than a player: their own table finished, or they hold no seat. */
export function observing(lobby: RoomLobby, playerId: string): boolean {
  return place(lobby, playerId).observing;
}

/**
 * Who this client pulls: v1's pullable members, narrowed to the room the
 * SCREEN is showing. The ears follow the eyes - a player still watching their
 * own table's podium keeps hearing it, and the server's grace window is what
 * lets that lag the room it would derive for them.
 *
 * Without the lobby document yet, everybody reads as being in the lobby's room,
 * which is right for every phase but a split round - and there the server
 * refuses what this would wrongly ask for, so the next snapshot corrects it.
 */
export function roomPulls(
  room: VoiceRoom,
  myId: string,
  isMuted: MuteCheck,
  lobby: RoomLobby | null,
  lobbyId: string,
  screenRoomId: string,
): string[] {
  const source = lobby ?? { id: lobbyId, phase: "" };
  // A Teams round is heard by team, not by the table on screen: a teammate at
  // another table is in my room and an opponent at mine is not. The server
  // judges the same room, so this asks for nothing it would refuse.
  if (teamsRound(source)) {
    const mine = roomOf(source, myId);
    return desiredPulls(room, myId, isMuted).filter(
      (id) => roomOf(source, id) === mine,
    );
  }
  // A table that has just finished is nobody's room any more - roomOf already
  // sends everyone who sat at it on to the next table still playing - but its
  // players are still looking at its podium together. Matching on roomOf alone
  // left that podium silent until the screens moved on. So a finished table on
  // screen means: the people who sat at it. The server allows exactly that,
  // because those players all derive to the same next room.
  const podium = lobby?.party?.instances.find(
    (i) => i.gameId === screenRoomId && i.status !== "playing",
  );
  if (podium)
    return desiredPulls(room, myId, isMuted).filter((id) =>
      podium.playerIds.includes(id),
    );
  return desiredPulls(room, myId, isMuted).filter(
    (id) => roomOf(source, id) === screenRoomId,
  );
}

/** What arriving in a room means for this player: the line they are shown, and whether their mic goes off. */
export type RoomArrival =
  | { kind: "lobby" }
  | { kind: "seated" }
  | { kind: "observer"; table: number }
  | { kind: "all-tables" }
  // A Teams round: a seated player is with their team wherever they look, and
  // everybody else is with the other watchers at every zoom (teamPlace).
  | { kind: "team"; team: string }
  | { kind: "watchers" };

/**
 * Which room the screen just moved this player into, from their point of view.
 *
 * Null means it cannot be told yet: the screen is showing an instance the
 * lobby document this client holds does not list, which is a directory that
 * has not caught up with the deal. Ask again on the next lobby snapshot rather
 * than guessing - guessing "seated" would leave an observer's mic on in a room
 * they just walked into.
 */
export function roomArrival(
  lobby: RoomLobby | null,
  lobbyId: string,
  myId: string,
  screenRoomId: string,
): RoomArrival | null {
  if (screenRoomId === lobbyId) return { kind: "lobby" };
  const instances = lobby?.party?.instances ?? [];
  const index = instances.findIndex((i) => i.gameId === screenRoomId);
  // Which table is on screen does not decide a Teams round's room, so the
  // table wording would be wrong there: say who is actually listening.
  const team = lobby ? teamPlace(lobby, myId) : null;
  if (team && (index >= 0 || screenRoomId === aloneRoom(myId))) {
    return team.observing
      ? { kind: "watchers" }
      : { kind: "team", team: team.room.slice("team:".length) };
  }
  if (screenRoomId === aloneRoom(myId)) return { kind: "all-tables" };
  if (index < 0) return null;
  if (instances[index]!.playerIds.includes(myId)) return { kind: "seated" };
  return { kind: "observer", table: index + 1 };
}

/**
 * The arrival to announce when the lobby document alone moved this player's
 * DERIVED room into or out of a Teams round, or null when it did not.
 *
 * Needed because at a single table the screen's room is the lobby id before,
 * during and after the round, so `roomArrival` (driven by the screen) never
 * fires when voice becomes team-only. Ordinary table rooms are not this
 * function's business: only a Teams round on either side of the change counts.
 */
export function derivedRoomArrival(
  prev: RoomLobby | null,
  next: RoomLobby,
  myId: string,
): RoomArrival | null {
  if (!prev || roomOf(prev, myId) === roomOf(next, myId)) return null;
  if (teamsRound(next)) {
    const placed = teamPlace(next, myId);
    if (!placed) return null;
    return placed.observing
      ? { kind: "watchers" }
      : { kind: "team", team: placed.room.slice("team:".length) };
  }
  if (teamsRound(prev) && roomOf(next, myId) === next.id)
    return { kind: "lobby" };
  return null;
}

/** The notice for an arrival. A moment, so it is the notice queue's rather than the HUD strip's. */
export function roomArrivalText(arrival: RoomArrival): string {
  switch (arrival.kind) {
    case "lobby":
      return "Back in the lobby";
    case "seated":
      return "Now talking with your table";
    case "observer":
      return `Watching table ${arrival.table} — your mic is off`;
    case "all-tables":
      return "Watching every table — voice is off";
    case "team":
      return `Now talking with the ${teamStyle(arrival.team)?.name ?? "your"} team`;
    case "watchers":
      return "Watching — only other watchers can hear you";
  }
}
